type ColorShade = {
  DEFAULT: string;
  hover: string;
  active: string;
  bg: string;
  text: string;
};

type ColorVariant = {
  light: ColorShade;
  dark: ColorShade;
};

type ColorScheme = {
  primary: ColorVariant;
  secondary: ColorVariant;
  success: ColorVariant;
  danger: ColorVariant;
  warning: ColorVariant;
  info: ColorVariant;
};

export const colors: ColorScheme = {
  primary: {
    light: {
      DEFAULT: '#042f8a',
      hover: '#0a3db0',
      active: '#032066',
      bg: '#e6ebf7',
      text: '#042f8a',
    },
    dark: {
      DEFAULT: '#1e4fbf',
      hover: '#3b6ad4',
      active: '#5f85e0',
      bg: '#021540',
      text: '#e6ebf7',
    }
  },
  secondary: {
    light: {
      DEFAULT: '#fcb903',
      hover: '#e5a600',
      active: '#cc9400',
      bg: '#fff8e6',
      text: '#7a5a00',
    },
    dark: {
      DEFAULT: '#fdcc3d',
      hover: '#fdd75e',
      active: '#fee28a',
      bg: '#5c4400',
      text: '#fff8e6',
    }
  },
  success: {
    light: {
      DEFAULT: '#16A34A', // green-600
      hover: '#15803D',   // green-700
      active: '#166534',  // green-800
      bg: '#F0FDF4',      // green-50
      text: '#16A34A',    // green-600
    },
    dark: {
      DEFAULT: '#22C55E', // green-500
      hover: '#4ADE80',   // green-400
      active: '#86EFAC',  // green-300
      bg: '#166534',      // green-900
      text: '#DCFCE7',    // green-100
    }
  },
  danger: {
    light: {
      DEFAULT: '#DC2626', // red-600
      hover: '#B91C1C',   // red-700
      active: '#991B1B',  // red-800
      bg: '#FEF2F2',      // red-50
      text: '#DC2626',    // red-600
    },
    dark: {
      DEFAULT: '#EF4444', // red-500
      hover: '#F87171',   // red-400
      active: '#FCA5A5',  // red-300
      bg: '#7F1D1D',      // red-900
      text: '#FEE2E2',    // red-100
    }
  },
  warning: {
    light: {
      DEFAULT: '#f8edae',
      hover: '#f5e38f',
      active: '#f2d970',
      bg: '#fffdf2',
      text: '#856404',
    },
    dark: {
      DEFAULT: '#f5e38f',
      hover: '#f2d970',
      active: '#efce51',
      bg: '#716412',
      text: '#fffdf2',
    }
  },
  info: {
    light: {
      DEFAULT: '#63d1e6',
      hover: '#40c5dd',
      active: '#26b5ce',
      bg: '#f0fbfd',
      text: '#005d6e',
    },
    dark: {
      DEFAULT: '#40c5dd',
      hover: '#26b5ce',
      active: '#1fa0b7',
      bg: '#103b44',
      text: '#f0fbfd',
    }
  }
};

// Helper function to convert hex to RGB values
export function hexToRgb(hex: string): [number, number, number] {
  // Remove the hash if it exists
  const cleanHex = hex.replace('#', '');
  
  // Parse the hex values
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  
  return [r, g, b];
}

// Helper function to convert RGB values to CSS variable format
export function rgbToCssVar(r: number, g: number, b: number): string {
  return `${r} ${g} ${b}`;
}

// Generate CSS variables for the color scheme
export function generateColorVariables(): Record<string, string> {
  const variables: Record<string, string> = {};
  
  Object.entries(colors).forEach(([colorName, variants]) => {
    Object.entries(variants).forEach(([variant, shades]) => {
      Object.entries(shades).forEach(([shade, value]) => {
        const [r, g, b] = hexToRgb(value);
        variables[`--color-${colorName}-${variant}-${shade}`] = rgbToCssVar(r, g, b);
      });
    });
  });

  return variables;
} 